import PropTypes from 'prop-types';
import React, { memo, useState, useEffect } from 'react';
import { Layout, Spin, Row, Divider } from 'antd';
import { useConversion, useRegistration, useAppContext, useVRNs } from '@send-data-hooks';
import { Link } from 'react-router-dom';
import { useFeature } from '@sendpayments/react-shared/hooks/useFeature';
import { PD_4724_USE_JPM_PARENT_ACCOUNT_DETAILS } from '@sendpayments/shared-constants/features';
import CustomLayout from '@send-legacy-components/Common/CustomLayout';
import { Icon } from '@send-legacy-components/Common';
import { Conversion } from '@send-legacy-containers/Conversion';
import { SMenu } from '@send-legacy-components/UI/SMenu';
import styles from './Conversion.module.less';
import history from '@send-legacy-app/history';
import { Confirmation as JPMConfirmation } from '@send-legacy-components/Common/Confirmation/Jpm/Confirmation';
import { saveEntity } from '@sendpayments/js-utils/dist/services/entities';
import { useConfirmation } from '@send-legacy-components/Common/Confirmation';
import { CoconutTypes } from '@sendpayments/shared-constants/types';
import dayjs from 'dayjs';
import { VRNAcknowledgementPanel } from '@send-legacy-components/VRN/VRNAcknowledgementPanel';

const { Content } = Layout;

const ConversionDetail = memo((props) => {
  const [{ data: reg }] = useRegistration();
  const [{ data: conv }] = useConversion({ id: props.match.params.id });
  const [{ data: virtualAccounts }] = useVRNs();
  const [collapsed, setCollapsed] = useState(true);
  const [{ loading: pageLoading }] = useAppContext();
  const urlParams = new URLSearchParams(history.location.search);
  const poliToken = urlParams.get('token');
  const [
    isVisibleUpdatedBankDetailsConfirmation,
    {
      setConfirmationVisible: setUpdatedBankDetailsConfirmationVisible,
      setConfirmationHidden: setUpdatedBankDetailsConfirmationHidden,
    },
  ] = useConfirmation();

  const [
    {
      data: { isEnabled: isUseJPMParentAccountEnabled },
    },
  ] = useFeature({
    feature: PD_4724_USE_JPM_PARENT_ACCOUNT_DETAILS,
    functionName: 'UseJPMParentAccount',
  });

  useEffect(() => {
    if (conv?.sellCurrency === 'AUD' && reg && !['/transfer', '/recipient/transfer'].includes(props?.location.state?.from)) {
      const relevantVRNs = Array.isArray(virtualAccounts) ? virtualAccounts?.filter((vrn) => vrn?.parentId === reg.id) : [];
      const jpmParentAccountAcknowledgement = JSON.parse(reg?.vrnLegalAcknowledgement ?? null)?.['PARENT_ACCOUNT']?.['AUD']?.LEGAL
        ? true
        : false;
      if (
        isUseJPMParentAccountEnabled &&
        !jpmParentAccountAcknowledgement &&
        reg?.type === CoconutTypes.RETAIL &&
        reg?.affiliateName?.toLowerCase() !== 'pexa' &&
        relevantVRNs?.length > 0
      )
        return setUpdatedBankDetailsConfirmationVisible();
    }
  }, [reg, conv, virtualAccounts]);

  const onContinuePayment = async () => {
    let newVRNLegalAcknowledgement = {};
    if (reg.vrnLegalAcknowledgement) {
      newVRNLegalAcknowledgement = JSON.parse(reg.vrnLegalAcknowledgement);
    }
    if (!newVRNLegalAcknowledgement.PARENT_ACCOUNT) {
      newVRNLegalAcknowledgement.PARENT_ACCOUNT = { AUD: {} };
    }

    newVRNLegalAcknowledgement.PARENT_ACCOUNT.AUD.LEGAL = dayjs().format();
    await saveEntity({
      entity: {
        id: reg.id,
        vrnLegalAcknowledgement: JSON.stringify(newVRNLegalAcknowledgement),
      },
      type: reg.type,
    });

    setUpdatedBankDetailsConfirmationHidden();
  };

  const onCancelJPMPArentAccountConfirmation = () => {
    setUpdatedBankDetailsConfirmationHidden();
  };

  return (
    <Spin spinning={pageLoading}>
      <CustomLayout onClick={() => setCollapsed(true)} showSupportButton>
        <Content>
          <div className="container">
            <Row>
              <SMenu registration={reg} collapsed={collapsed} setCollapsed={setCollapsed} />
            </Row>
            <Divider className="top-menu-divider" />
            <VRNAcknowledgementPanel staticEnabled />
            <Row className={styles.margin}>
              <Link className={styles.link} to={`/activity`}>
                <Icon type="left" />
                Your activities
              </Link>
            </Row>
            {isVisibleUpdatedBankDetailsConfirmation && (
              <JPMConfirmation
                okButtonText="I acknowledge these changes"
                onOkClick={onContinuePayment}
                onCancelClick={onCancelJPMPArentAccountConfirmation}
              />
            )}
            <Conversion
              id={props.match.params.id}
              currencyCloudReference={reg.currencyCloudReference}
              sendId={reg.sendId}
              poliToken={poliToken}
            />
          </div>
        </Content>
      </CustomLayout>
    </Spin>
  );
});

ConversionDetail.propTypes = {
  match: PropTypes.any.isRequired,
  location: PropTypes.any,
};

export default ConversionDetail;
